// Vendor imports
const _ = require('lodash')
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { imageLayers } = require('shared/css-utils')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const ViewportObserver = require('client-v2/common/utils/viewport-observer')

const bem = BemEntity('LazyImage')

let viewportObserver
if (typeof window === 'object') {
  viewportObserver = new ViewportObserver({
    callback: (domElement, reactElement) => { reactElement.setVisible() },
    intersectionObserverOptions: {
      root: null,
      rootMargin: '200px',
      threshold: .2
    }
  })
}

class LazyImage extends React.PureComponent {
  constructor(props) {
    super(props)

    this.ref = React.createRef()
    this.imageRef = React.createRef()
    this.state = {
      cached: false,
      loaded: false,
      sourceIndex: 0,
      sources: normalizeSources(props.sources),
      visible: props.visible
    }
    this.onImageError = this.onImageError.bind(this)
    this.onImageLoad = this.onImageLoad.bind(this)
  }

  componentDidMount() {
    this.observeVisibility()
    const imageEl = this.imageRef.current
    if (this.state.visible && imageEl) {
      const cached = (
        imageEl.complete ||
        imageEl.readyState === 4 ||
        imageEl.readyState === 'complete'
      )
      if (cached) {
        this.setState(() => ({ cached: true, loaded: true }))
      }
    }
  }

  componentWillUnmount() {
    this.unobserveVisibility()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.visible !== this.props.visible) {
      this.setState(
        (state, props) => ({ visible: props.visible }),
        () => this.observeVisibility()
      )
    }
    if (!_.isEqual(prevProps.sources, this.props.sources)) {
      this.setState((state, props) => {
        // We don't get the image load event if it doesn't change
        const prevLoadedSource = state.sources[state.sourceIndex]
        const sources = normalizeSources(props.sources)
        return {
          loaded: state.visible && prevLoadedSource === sources[0],
          sourceIndex: 0,
          sources
        }
      })
    }
  }

  observeVisibility() {
    const el = this.ref.current
    if (!this.state.visible && viewportObserver && el) {
      viewportObserver.observe(el, this)
    }
  }

  unobserveVisibility() {
    const el = this.ref.current
    if (viewportObserver && el) {
      viewportObserver.unobserve(el)
    }
  }

  setVisible() {
    this.setState(() => ({ visible: true }))
  }

  // Event handler methods

  onImageError() {
    this.setState(({ sourceIndex }) => ({ sourceIndex: sourceIndex + 1 }))
  }

  onImageLoad() {
    this.setState(() => ({ loaded: true }))
  }

  // Prop methods

  loading() {
    return this.state.visible && !!this.src() && !this.state.loaded
  }

  src() {
    if (this.state.visible) {
      return this.state.sources[this.state.sourceIndex]
    }
  }

  style() {
    /**
     * Override `background-image` provided by `placeholder` prop or CSS.
     * This prevents images with alpha channel from looking bad.
     * @see https://app.asana.com/0/102902769366319/1128570656009822/f
     */
    if (this.state.loaded && this.props.hidePlaceholderAfterLoaded) {
      return { backgroundImage: 'none' }
    }
    const backgroundImage = imageLayers(this.props.placeholder)
    if (backgroundImage) {
      return { backgroundImage }
    }
  }

  // Render methods

  renderImage() {
    const src = this.src()
    if (!src) {
      return null
    }
    return (
      tag('img', {
        ref: this.imageRef,
        className: bem.element('image', {
          cached: this.state.cached,
          loaded: this.state.loaded,
          loading: this.loading()
        }),
        src,
        onError: this.onImageError,
        onLoad: this.onImageLoad
      })
    )
  }

  render() {
    const props = _.omit(this.props, LazyImage.reservedPropKeys)
    const tagName = props.href || props.onClick ? 'a' : 'div'
    return (
      tag(tagName, {
        ...props,
        ref: this.ref,
        className: classNames(
          props.className,
          bem.block({
            loading: this.loading()
          })
        ),
        style: {
          ...props.style,
          ...this.style()
        }
      },
        this.renderImage()
      )
    )
  }
}

// These prop keys will not get passed into DOM element
LazyImage.reservedPropKeys = [
  'children',
  'hidePlaceholderAfterLoaded',
  'placeholder',
  'sources',
  'visible'
]

LazyImage.propTypes = {
  // NOTE: Passes down all other props to its DOM node
  className: PropTypes.string,
  hidePlaceholderAfterLoaded: PropTypes.bool,
  placeholder: PropTypes.string,
  sources: PropTypes.arrayOf(PropTypes.string).isRequired,
  visible: PropTypes.bool
}

LazyImage.defaultProps = {
  hidePlaceholderAfterLoaded: true,
  visible: false
}

module.exports = LazyImage

function normalizeSources(sources) {
  return _.chain(sources)
  .uniq()
  .filter()
  .value()
}
